import { useEffect, type RefObject } from "react";

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isFocusableElementVisible = (element: HTMLElement): boolean => {
    if (element.getAttribute("aria-hidden") === "true") {
        return false;
    }

    return element.getClientRects().length > 0;
};

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isFocusableElementVisible);

type UseAfterTrialEntryModalFocusTrapParams = {
    containerRef: RefObject<HTMLElement | null>;
    isEnabled: boolean;
};

export const useAfterTrialEntryModalFocusTrap = ({
    containerRef,
    isEnabled,
}: UseAfterTrialEntryModalFocusTrapParams): void => {
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        const container = containerRef.current;
        if (!container) {
            return;
        }

        const previouslyFocusedElement = document.activeElement as HTMLElement | null;

        const focusInitialElement = () => {
            const focusableElements = getFocusableElements(container);
            focusableElements[0]?.focus();
        };

        requestAnimationFrame(focusInitialElement);

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Tab") {
                return;
            }

            const focusableElements = getFocusableElements(container);
            if (focusableElements.length === 0) {
                event.preventDefault();
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement as HTMLElement | null;

            if (event.shiftKey) {
                if (activeElement === firstElement || (activeElement && !container.contains(activeElement))) {
                    event.preventDefault();
                    lastElement.focus();
                }

                return;
            }

            if (activeElement === lastElement || (activeElement && !container.contains(activeElement))) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            previouslyFocusedElement?.focus?.();
        };
    }, [containerRef, isEnabled]);
};
